// Shared data model for every format parser.
//
// A parser turns the raw bytes of a file into a tree of Segments. Each Segment
// is a byte range [start, end) with a kind (which sets its colour in the UI), a
// type key (which selects its explanation text), and optional fields. Segments
// that hold encoded audio also carry a time range, which is what lets the UI
// draw the link between "where in the file" and "when in the sound".

export type SegmentKind =
  | 'container' // a wrapper whose bytes are mostly its children (RIFF, moov, an Ogg page)
  | 'header' // stream / format description (fmt, STREAMINFO, OpusHead, ftyp, frame headers)
  | 'metadata' // human tags (ID3, Vorbis comments, LIST/INFO, ilst)
  | 'image' // embedded picture bytes (cover art)
  | 'index' // seek tables, sample tables, Xing TOC — data that helps find other data
  | 'audio' // encoded or raw audio payload
  | 'padding' // intentionally empty space reserved for later edits
  | 'unknown'; // bytes the parser does not recognize

/**
 * One named value inside a segment. Its location is given in absolute bits so
 * the UI can draw both byte-aligned fields and packed bit fields (like an MP3
 * frame header) with one code path.
 */
export interface Field {
  name: string;
  /** Formatted value as shown to the user, e.g. "44,100 Hz" or "Joint stereo". */
  value: string;
  /** Raw numeric value when there is one (before lookup tables / formatting). */
  raw?: number | bigint;
  /** Absolute bit position of the first bit (byteOffset * 8 + bitWithinByte, MSB first). */
  bit: number;
  /** Length in bits. Byte-aligned fields use multiples of 8. */
  bits: number;
  /** One short sentence that explains what this field means. Plain English. */
  note?: string;
}

export interface Segment {
  kind: SegmentKind;
  /** Short label shown in the tree and on hover: "fmt chunk", "Frame 12", "ID3v2.4 tag". */
  name: string;
  /**
   * Stable key used to look up the explanation text for this kind of block,
   * e.g. "mp3.frame", "wav.fmt", "id3v2.frame.APIC". Keys are namespaced by parser.
   */
  type: string;
  /** Absolute byte offsets, end exclusive. */
  start: number;
  end: number;
  /** For segments that hold audio: the time span, in seconds, that these bytes produce. */
  time?: [number, number];
  /** One-line human summary shown next to the name: "128 kbps · 44.1 kHz · Joint stereo". */
  summary?: string;
  /** Fields may be computed lazily (thousands of frames each have ~15 fields). */
  fields?: Field[] | (() => Field[]);
  /** Children may be computed lazily. Children must lie inside [start, end) and be sorted by start. */
  children?: Segment[] | (() => Segment[]);
  /** Present when the bytes of this segment (or a sub-range) are an image the UI can show. */
  image?: { mime: string; start: number; end: number };
  /** Something unusual about this block, shown with a warning marker. */
  warning?: string;
  /**
   * Other byte ranges this block depends on or points to, drawn as arcs in the
   * byte map when the block is selected. Examples: an MP3 frame whose audio data
   * starts in earlier frames (bit reservoir); an MP4 sample and its entries in
   * the stsz / stco tables. Lazy, since it is only needed for the selection.
   */
  links?: () => SegmentLink[];
}

export interface SegmentLink {
  /** Short label drawn on the arc, e.g. "audio data starts here" or "size in stsz". */
  label: string;
  start: number;
  end: number;
}

export interface AudioInfo {
  /** Short codec name: "MP3", "AAC-LC", "HE-AAC", "FLAC", "Vorbis", "Opus", "PCM", "ALAC", "MP2". */
  codec: string;
  /** Longer description: "MPEG-1 Audio Layer III", "Linear PCM, 16-bit signed integer". */
  codecLong?: string;
  sampleRate?: number;
  channels?: number;
  /** Bit depth for PCM / lossless formats. Undefined for lossy formats. */
  bitsPerSample?: number;
  /** e.g. "Stereo", "Joint stereo", "Mono", "5.1". */
  channelMode?: string;
  /** Total duration in seconds, as described by the file. */
  duration?: number;
  totalSamples?: number;
  /** Average bitrate of the audio payload in bits per second. */
  bitrate?: number;
  bitrateMode?: 'CBR' | 'VBR' | 'ABR' | 'Lossless' | 'Uncompressed';
  lossless: boolean;
  /** Encoder string when the file names one (LAME version, Vorbis vendor, ffmpeg "Lavf"). */
  encoder?: string;
  /** Samples of silence the encoder added at the start / end (gapless info), if known. */
  encoderDelay?: number;
  encoderPadding?: number;
}

export interface Tag {
  /** Normalized, human key: "Title", "Artist", "Album", "Year", "Track", "Genre", "Comment"... */
  key: string;
  value: string;
  /** The key as written in the file: "TIT2", "TITLE", "©nam", "INAM". */
  rawKey: string;
}

export interface Picture {
  mime: string;
  /** Byte range of the image data itself. */
  start: number;
  end: number;
  description?: string;
  /** "Front cover" etc. */
  pictureType?: string;
}

export interface Note {
  level: 'info' | 'warn';
  /** One or two short sentences, plain English. Shown in the "Observations" list. */
  text: string;
}

export type FormatId = 'mp3' | 'mp2' | 'aac' | 'wav' | 'aiff' | 'flac' | 'ogg' | 'mp4';

export interface ParsedFile {
  format: FormatId;
  /** Container name for display: "MP3", "WAV (RIFF)", "FLAC", "Ogg", "MPEG-4 (M4A)", "AIFF", "ADTS". */
  container: string;
  size: number;
  /** Top-level segments in byte order. The app fills any gaps with 'unknown' segments. */
  segments: Segment[];
  /**
   * Every timed audio unit (MP3 frames, FLAC frames, Ogg audio pages, MP4 samples,
   * the WAV data chunk) in byte order. These must also appear somewhere in the
   * segment tree. The UI uses them to map bytes to time and back; time is assumed
   * to be linear within each unit.
   */
  frames: Segment[];
  audio: AudioInfo;
  tags: Tag[];
  pictures: Picture[];
  /** Observations worth teaching: VBR detected, moov at end, encoder delay, etc. */
  notes: Note[];
  /** For uncompressed PCM (WAV/AIFF): where the samples are and how to read them. */
  pcm?: PcmLayout;
}

export interface PcmLayout {
  dataStart: number;
  dataEnd: number;
  channels: number;
  bitsPerSample: number;
  /** Bytes per sample frame (all channels). */
  blockAlign: number;
  float: boolean;
  bigEndian: boolean;
  sampleRate: number;
}

/** Text shown when a segment of a given type is selected. */
export interface Explanation {
  /** Heading, e.g. "MPEG audio frame". */
  title: string;
  /** 1–4 short sentences, plain friendly English. May use **bold** and `code`. */
  body: string;
}

export interface FormatParser {
  id: FormatId;
  /** Returns true if the bytes look like this format. Checks magic numbers, not file names. */
  detect(bytes: Uint8Array): boolean;
  parse(bytes: Uint8Array): ParsedFile;
  /** Explanations for every `type` key this parser emits. */
  explanations: Record<string, Explanation>;
}

export class ParseError extends Error {}
